// frontend/admin-portal/src/pages/OrderDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Spinner,
  Alert,
  formatMoney,
} from "@fiberhome/shared-ui";
import { orderService } from "../services/orderService";
import { resolveImageUrl } from "../services/fileService";
import { useStoreIdentity } from "../hooks/useStoreIdentity";
import OrderStatusBadge from "../components/orders/OrderStatusBadge";
import OrderExportMenu from "../components/orders/OrderExportMenu";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const store = useStoreIdentity();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await orderService.get(id);
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await fn();
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Order" />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <PageHeader title="Not found" />
        <Alert variant="danger">{error || "Order not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/orders")}
        >
          ← Back
        </Button>
      </>
    );
  }

  const cur = order.currency || "USD";
  const isOffer = order.kind === "offer";
  const canSend = order.status === "draft";
  const canAccept = ["draft", "offer_sent"].includes(order.status);
  const canReject = ["offer_sent", "accepted"].includes(order.status);
  const canFulfill = order.status === "accepted";
  const canCancel = !["fulfilled", "cancelled"].includes(order.status);
  const canDelete = order.status === "draft";

  return (
    <>
      <PageHeader
        title={`${order.code} — ${isOffer ? "Offer" : "Order"}`}
        subtitle={order.customerName || order.customer?.name}
        breadcrumbs={["Home", "Sales", "Offers & Orders", order.code]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <OrderExportMenu order={order} />
            <Button
              variant="primary"
              onClick={() => navigate(`/orders/${order.id}/print`)}
            >
              🖨 Print
            </Button>
            {canSend && (
              <Button
                variant="outline"
                onClick={() =>
                  run(() => orderService.send(order.id), "Send this offer?")
                }
                disabled={busy}
              >
                Send Offer
              </Button>
            )}
            {canAccept && (
              <Button
                variant="primary"
                onClick={() =>
                  run(() => orderService.accept(order.id), "Mark as accepted?")
                }
                disabled={busy}
              >
                Accept
              </Button>
            )}
            {canReject && (
              <Button
                variant="ghost"
                onClick={() =>
                  run(
                    () =>
                      orderService.reject(
                        order.id,
                        window.prompt("Reason:") || "",
                      ),
                    "Mark as rejected?",
                  )
                }
                disabled={busy}
              >
                Reject
              </Button>
            )}
            {canFulfill && (
              <Button
                variant="success"
                onClick={() =>
                  run(
                    () => orderService.fulfill(order.id),
                    "Mark as fulfilled?",
                  )
                }
                disabled={busy}
              >
                Fulfill
              </Button>
            )}
            {canCancel && (
              <Button
                variant="ghost"
                onClick={() =>
                  run(
                    () =>
                      orderService.cancel(
                        order.id,
                        window.prompt("Reason:") || "",
                      ),
                    "Cancel this?",
                  )
                }
                disabled={busy}
              >
                Cancel
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                onClick={() =>
                  run(
                    () =>
                      orderService
                        .remove(order.id)
                        .then(() => navigate("/orders")),
                    "Delete this draft?",
                  )
                }
                disabled={busy}
              >
                Delete
              </Button>
            )}
          </div>
        }
      />

      {/* ---------- Store identity banner ---------- */}
      <div className="fh-order-store-banner">
        <div className="fh-order-store-banner__left">
          {store.logo ? (
            <img
              src={resolveImageUrl(store.logo)}
              alt={store.name}
              className="fh-order-store-banner__logo"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="fh-order-store-banner__logo-placeholder">
              {store.name?.[0]?.toUpperCase() || "F"}
            </div>
          )}
          <div>
            <div className="fh-order-store-banner__name">{store.name}</div>
            {store.legalName && (
              <div className="fh-order-store-banner__legal">
                {store.legalName}
              </div>
            )}
            {store.address && (
              <div className="fh-order-store-banner__meta">{store.address}</div>
            )}
            {[store.phone, store.email, store.website].filter(Boolean).length >
              0 && (
              <div className="fh-order-store-banner__meta">
                {[store.phone, store.email, store.website]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
            {store.taxId && (
              <div className="fh-order-store-banner__meta">
                Tax ID: {store.taxId}
              </div>
            )}
          </div>
        </div>
        <div className="fh-order-store-banner__right">
          <div className="fh-order-store-banner__label">
            {isOffer ? "QUOTATION" : "SALES ORDER"}
          </div>
          <div className="fh-order-store-banner__code">{order.code}</div>
        </div>
      </div>

      {/* ---------- Status / Customer / Totals ---------- */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Card title="Status">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Kind</span>
                <span>{isOffer ? "Offer" : "Order"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Status</span>
                <OrderStatusBadge status={order.status} />
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Issued</span>
                <span>{new Date(order.issuedAt).toLocaleDateString()}</span>
              </div>
              {order.validUntil && (
                <div className="fh-kv__row">
                  <span className="text-muted">Valid until</span>
                  <span>{order.validUntil}</span>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="col-md-4">
          <Card title="Customer">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Name</span>
                <span>{order.customerName}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Phone</span>
                <span>{order.customerPhone || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Email</span>
                <span>{order.customerEmail || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Address</span>
                <span style={{ textAlign: "right", fontSize: 12 }}>
                  {order.customerAddress || "—"}
                </span>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-md-4">
          <Card title="Totals">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Subtotal</span>
                <span>{formatMoney(order.subtotal, { symbol: cur })}</span>
              </div>
              {Number(order.discountAmount) > 0 && (
                <div className="fh-kv__row">
                  <span className="text-muted">
                    Discount ({order.discountPercent}%)
                  </span>
                  <span>
                    -{formatMoney(order.discountAmount, { symbol: cur })}
                  </span>
                </div>
              )}
              {Number(order.taxAmount) > 0 && (
                <div className="fh-kv__row">
                  <span className="text-muted">Tax ({order.taxRate}%)</span>
                  <span>{formatMoney(order.taxAmount, { symbol: cur })}</span>
                </div>
              )}
              {Number(order.shipping) > 0 && (
                <div className="fh-kv__row">
                  <span className="text-muted">Shipping</span>
                  <span>{formatMoney(order.shipping, { symbol: cur })}</span>
                </div>
              )}
              <div className="fh-kv__row">
                <span className="text-muted">
                  <strong>Total</strong>
                </span>
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  {formatMoney(order.totalAmount, { symbol: cur })}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ---------- Items ---------- */}
      <Card title={`Items (${order.items?.length || 0})`} className="mb-3">
        <div className="fh-table-wrap">
          <table className="fh-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Unit Price</th>
                <th>Disc</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((it, i) => (
                <tr key={it.id}>
                  <td>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{it.productName}</div>
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      {it.productSku}
                    </div>
                  </td>
                  <td>{it.quantity}</td>
                  <td>{it.unit}</td>
                  <td>{formatMoney(it.unitPrice, { symbol: cur })}</td>
                  <td>
                    {Number(it.discountPercent) > 0
                      ? `${it.discountPercent}%`
                      : "—"}
                  </td>
                  <td>
                    <strong>
                      {formatMoney(it.lineTotal, { symbol: cur })}
                    </strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---------- Notes ---------- */}
      {order.notes && (
        <Card title="Notes" className="mb-3">
          <div style={{ whiteSpace: "pre-wrap" }}>{order.notes}</div>
        </Card>
      )}

      {order.termsAndConditions && (
        <Card title="Terms & Conditions">
          <div style={{ whiteSpace: "pre-wrap", fontSize: 12.5 }}>
            {order.termsAndConditions}
          </div>
        </Card>
      )}
    </>
  );
}
